import {globalSetErrors} from "./apiErrors.jsx";
import {toast} from "react-toastify";
import axios from 'axios';
import {AdminApi, DatabaseConnection, EmailRequest, ProtectedApi, PublicApi, UserResponse} from "./api-client/api.js";
import applyCaseMiddleware from "axios-case-converter";

class Api {

    axios_client = null;
    protectedClient = null;
    publicClient = null;
    adminClient = null;

    constructor() {
        this.resetClient()
    }

    resetClient() {
        function _getAPIBaseUrl() {
            if (window.location.hostname === "localhost") {
                return "http://localhost:2000"
            }
            return "https://api.veilstream.com"
        }
        this.axios_client = axios.create({
            baseURL: _getAPIBaseUrl(),
            withCredentials: true, // Sends cookies automatically
            headers: {'X-API-Key': ""}
        });
        this.axios_client = applyCaseMiddleware(this.axios_client); // Converts snake_case to camelCase
        this.axios_client.interceptors.response.use(
            response => response,
            error => {
                if (error.response?.status === 422) {
                    const validationErrors = error.response.data.detail;
                    globalSetErrors(validationErrors);
                }
                if (error.response?.status === 401) {
                    localStorage.removeItem("profile")
                    window.location.href = "/login"
                }
                return Promise.reject(error);
            }
        );

        this.protectedClient = new ProtectedApi(undefined, _getAPIBaseUrl(), this.axios_client);
        this.publicClient = new PublicApi(undefined, _getAPIBaseUrl(), this.axios_client);
        this.adminClient = new AdminApi(undefined, _getAPIBaseUrl(), this.axios_client)
    }

    createDatabase(request: { name: string, hostingMode: string, region: string }) {
        return this.protectedClient.createServerServerPost(request).then((response) => {
            toast.success("'"+request.name+"' database created")
            return response.data
        }).catch(e => {
            toast.warn("Error creating database")
            console.error("Error creating database")
        })
    }

    getDatabase(DatabaseId: string) {
        return this.protectedClient.getServerServerServerIdGet(DatabaseId).then(response => {
            return response.data
        })
    }

    getHostedProxy(DatabaseId: string) {
        return this.protectedClient.getHostedProxyHostedProxyServerIdGet(DatabaseId).then(response => {
            return response.data
        })
    }

    async getSchemaChanges(DatabaseId: string) {
        return this.protectedClient.getSchemaChangesDatabaseServerIdSchemaChangesGet(DatabaseId).then(response => {
            return response.data
        })
    }

    async listDatabases() {
        return await this.protectedClient.listServersServersGet().then(data => {
            return data.data
        })
    }

    updateDatabase(DatabaseId:string, database: {
        name: string,
        description: string,
        hosting_mode: string,
        on_schema_change: string,
        role_ids: Array<string>,
        database_connection: DatabaseConnection,
        schema_allow_list: Array<string>,

    }) {
        return this.protectedClient.updateServerServerServerIdPut(DatabaseId, database).then(r => {
            toast.success("'"+database.name+"' updated")
            return r.data
        }).catch(e => {
            toast.warn("Error updating database")
            console.error("Error updating database: ", e)
        })
    }

    deleteDatabase(DatabaseId:string) {
        return this.protectedClient.deleteServerServerServerIdDelete(DatabaseId).then(r => {
            toast.success("Database deleted")
            return r.data
        }).catch(e => {
            toast.warn("Error deleting database")
        })
    }

    getDatabaseConfiguration(DatabaseId:string) {
        return this.protectedClient.getServerConfigServerServerIdConfigurationGet(DatabaseId).then(r => {
            return r.data
        })
    }

    createApiKey(DatabaseId:string) {
        return this.protectedClient.createApiKeyServerServerIdApiKeyPost(DatabaseId).then(r => {
            toast.success("API Key created")
            return r.data
        }).catch(e => {
            toast.warn("Error creating API Key")
        })
    }

    deleteApiKey(DatabaseId:string, apiKeyId:string) {
        return this.protectedClient.deleteApiKeyServerServerIdApiKeyApiKeyIdDelete(DatabaseId, apiKeyId).then(r => {
            toast.success("API Key deleted")
            return r.data
        }).catch(e => {
            toast.warn("Error deleting API Key")
        })
    }

    createFilter(filter: { name: string, description: string, databaseId: string, filter: never }) {
        return this.protectedClient.createFilterFilterPost(filter).then(r => {
            toast.success("'"+filter.name+"' filter created")
            return r.data
        }).catch(e => {
            toast.warn("Error creating filter")
            console.error("Error creating filter")
        })
    }

    getFilter(filterId:string): Promise<never> {
        return this.protectedClient.getFilterFilterFilterIdGet(filterId).then(r => {
            return r.data
        }).catch(e => {
            toast.warn("Error getting filter")
        })
    }

    listFilters(archived:boolean = false): Promise<Array<never>> {
        return this.protectedClient.getFiltersFiltersGet({
            archived: archived
        }).then(r => {
            return r.data
        }).catch(e => {
            toast.warn("Error getting filters")
        })
    }

    updateFilter(filterId:string, filter: { name: string, description: string, databaseId: string, filter: never }) {
        return this.protectedClient.updateFilterFilterFilterIdPost(filterId, filter).then(r => {
            toast.success("'"+filter.name+"' filter updated")
            return r.data
        }).catch(e => {
            toast.warn("Error updating filter")
            console.error("Error updating filter")
        })
    }

    createSyntheticUser(syntheticUser: never): never {
        return this.protectedClient.createSyntheticUserSyntheticUserPost(syntheticUser).then(r => {
            toast.success("User created")
            return r.data
        }).catch(e => {
            if (e.status === 409) {
                toast.warn("User name already in use")
            } else {
                toast.warn("Error updating User")
            }
        })
    }

    getSyntheticUser(syntheticUserId: string):Promise<never> {
        return this.protectedClient.getSyntheticUserSyntheticUserSyntheticUserIdGet(syntheticUserId).then(r => {
            return r.data
        }).catch(e => {
            toast.warn("Error getting User")
        })
    }

    listSyntheticUsers(): Promise<Array<never>> {
        return this.protectedClient.listSyntheticUsersSyntheticUsersGet().then(r => {
            return r.data
        }).catch(e => {
            toast.warn("Error getting Users")
        })
    }

    updateSyntheticUser(syntheticUserId: string, syntheticUser:never) {
        return this.protectedClient.updateSyntheticUserSyntheticUserSyntheticUserIdPut(syntheticUserId, syntheticUser).then(r => {
            toast.success("User updated")
            return r.data
        }).catch(e => {
            if (e.status === 409) {
                toast.warn("User name already in use")
            } else {
                toast.warn("Error updating User")
            }
        })
    }

    updateSyntheticUserPassword(syntheticUserId: string, password: string) {
        return this.protectedClient.updateSyntheticUserPasswordSyntheticUserSyntheticUserIdPasswordPost(syntheticUserId, {password: password}).then(r => {
            toast.success("User password updated")
            return r.data
        }).catch(e => {
            toast.warn("Error updating User password")
        })
    }

    deleteSyntheticUser(syntheticUserId: string): null {
        return this.protectedClient.deleteSyntheticUserSyntheticUserSyntheticUserIdDelete(syntheticUserId).then(r => {
            toast.success("User deleted")
            return r.data
        }).catch(e => {
            toast.warn("Error deleting User")
        })
    }

    deleteFilter(filterId: string) {
        return this.protectedClient.deleteFilterFilterFilterIdDelete(filterId).then(r => {
            toast.success("Filter deleted")
            return r.data
        }).catch(e => {
            toast.warn("Error deleting filter")
        })
    }

    createUser(request: never): any {
        return this.protectedClient.createUserUsersPost(request).then(r => {
            toast.success(`${r.data.email} invited to join your team!`)
            return r.data
        }).catch((e) => {
            if (e.response.status === 400) {
                toast.warn("Email associated with an existing organization")
            } else if (e.response.status === 409) {
                toast.warn("User already a member of your organization")
            } else {
                toast.warn("Error creating User")
            }
        })
    }

    getUser(userId: string): Promise<never> {
        return this.protectedClient.getUserUserUserIdGet(userId).then(r => {
            return r.data
        }).catch(e => {
            toast.warn("Error getting User")
        })
    }

    async getUserByEmail(email: string): Promise<UserResponse> {
        const allUsers = await this.listUsers()
        return allUsers.find((user: UserResponse) => user.email === email)
    }

    async getCurrentUser(): Promise<UserResponse> {
        const profile = localStorage.getItem("profile")
        const email = JSON.parse(profile)?.profile?.email
        if (email === undefined || email === null) {
            return null
        }
        return this.getUserByEmail(email)
    }

    deleteUser(userId: string): Promise<never> {
        return this.protectedClient.deleteUserUserUserIdDelete(userId).then(r => {
            toast.success("User deleted")
            return r.data
        }).catch(e => {
            if (e.response.status === 401) {
                toast.warn("Unauthorized: cannot delete owner")
            } else {
                toast.warn("Error deleting User")
            }
        })
    }

    listUsers(): Promise<Array<never>> {
        return this.protectedClient.getUsersUsersGet().then(r => {
            return r.data
        }).catch(e => {
            toast.warn("Error getting Users")
        })
    }

    inviteUser(request: EmailRequest): Promise<never> {
        return this.protectedClient.inviteUserEmailSendPost(request).then(r => {
            toast.success(`Invitation email sent to ${request.recipient}`)
            return r.data
        }).catch(e => {
            toast.warn("Error sending invitation email")
        })
    }

    updateOrganizationOwner(userId: string): Promise<never> {
        return this.protectedClient.changeOwnerUserUserIdChangeOwnerPost(userId).then(r => {
            toast.success(`Ownership permissions transferred to ${r.data.email}`)
            return r.data
        }).catch(e => {
           toast.warn("Error updating User")
        })
    }

    deleteOrganization(): Promise<never> {
        return this.protectedClient.deleteOrgOrganizationDelete().then(r => {
            toast.success("Organization deleted")
            return r.data
        }).catch(e => {
            toast.warn("Error deleting Organization")
        })
    }

    getError(){
        return this.publicClient.createErrorDebugErrorGet()
    }

    getBillingUsageStats(year, month) {
        return this.protectedClient.billingUsageBillingUsageGet({year: year, month: month}).catch(e => {
            toast.warn("Error getting billing usage stats")
        })
    }

    getBillingPlans() {
        return this.protectedClient.getPricesStripePricesGet().catch(e => {
            toast.warn("Error getting billing plans")
        })
    }

    getCheckoutUrl(priceId) {
        return this.protectedClient.checkoutStripeCheckoutGet({priceId: priceId}).catch(e => {
            toast.warn("Error getting checkout URL")
        })
    }

    async getLogin(googleToken) {
        if (googleToken === null) {
            return
        }
        return this.publicClient.loginLoginPost({
            credential: googleToken.credential,
            clientId: googleToken.clientId,
        }).catch(e => {
            console.log(e)
            toast.warn("Error logging in")
        }).then(data => {
            return data.data
        })
    }

    async getLatestDockerImage() {
        return this.publicClient.getLatestPublishedImageProxyLatestPublishedImageGet().then(data => {
            return data.data
        }).catch(e => {
            toast.warn("Error getting latest docker image")
        })
    }

    async getLoginDebug() {
        return this.publicClient.loginLoginPost({
            credential: "debug",
            clientId: "debug",
        }).then(data => {
            return data.data
        })
    }

    async listOrgs() {
        return this.adminClient.listOrganizationsOrganizationsGet().then(data => {
            return data.data
        })
    }

    async listSubscriptions() {
        return this.adminClient.getOrganizationBillingOrganizationsBillingGet().then(data => {
            return data.data
        }).catch(e => {
            if (e.response?.status === 404) {
                return null
            }
            toast.warn("Error getting subscriptions")
        })
    }

    acceptToS() {
        return this.protectedClient.acceptTocAcceptTocPost().catch(e => {
            toast.warn("Error accepting ToS")
        }).then(r => {
            return r.data
        })
    }

    async verifyJWTNotExpired() {
        return await this.publicClient.verifyJwtVerifyJwtPost().then(data => {
            return data
        })
    }

    async deleteRole(id: string) {
        return this.protectedClient.deleteRoleRoleRoleIdDelete(id).then(r => {
            toast.success("Role deleted")
            return r.data
        }).catch(e => {
            toast.warn("Error deleting role")
        })

    }

    async listRoles() {
        return this.protectedClient.listRolesRolesGet().then(r => {
            return r.data
        }).catch(e => {
            toast.warn("Error getting roles")
        })
    }

    async createRole(param: { name: string; description: string }) {
        return this.protectedClient.createRoleRolePost(param).then(r => {
            toast.success("Role created")
            return r.data
        }).catch(e => {
            if (e.status === 409){
                toast.warn("Role name already in use")
            } else {
                toast.warn("Error creating role")
            }
        })
    }

    async getBillingDetails(organizationId:string) {
        return this.protectedClient.getOrganizationBillingOrganizationsOrganizationIdBillingGet(organizationId).then(r => {
            return r.data
        }).catch(e => {
            if (e.response?.status === 404) {
                return null // No billing details found
            }
            toast.warn("Error getting billing details")
        })
    }
}

const apiClient = new Api()
export default apiClient;
